// PaymentSettings.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import {
  GET_BUSINESS_PAYOUT,
  SAVE_BUSINESS_PAYOUT,
} from "../../../graphql/queries";
import { useSelectedBusiness } from "../../../redux/store";

export default function PaymentSettings({ isDisabled }) {
  const selectedBusiness = useSelectedBusiness();
  const businessId = selectedBusiness?.businessBasic?.id;

  const { data } = useQuery(GET_BUSINESS_PAYOUT, {
    variables: { businessId },
    skip: !businessId,
  });

  const [savePayout, { loading: saving, error }] =
    useMutation(SAVE_BUSINESS_PAYOUT);

  /* ================= STATE ================= */
  const [provider, setProvider] = useState("PAYPAL");
  const [identifier, setIdentifier] = useState("");

  // baseline
  const [initialProvider, setInitialProvider] = useState(null);
  const [initialIdentifier, setInitialIdentifier] = useState(null);

  useEffect(() => {
    if (data?.getBusinessPayout) {
      const { provider, identifier } = data.getBusinessPayout;

      setProvider(provider);
      setIdentifier(identifier);

      setInitialProvider(provider);
      setInitialIdentifier(identifier);
    }
  }, [data]);

  const hasChanges =
    provider !== initialProvider ||
    identifier !== initialIdentifier;

  if (!businessId) {
    return (
      <Alert severity="warning">
        Select a business before configuring payout settings.
      </Alert>
    );
  }

  const handleSave = async () => {
    try {
      const { data } = await savePayout({
        variables: { businessId, provider, identifier },
      });

      // reset baseline after save
      setInitialProvider(provider);
      setInitialIdentifier(identifier);

      console.log("Payout settings saved:", data);
    } catch (err) {
      console.error("Error saving payout settings:", err);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Payout Settings
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure how you receive payments from your customers.
      </Typography>

      <TextField
        select
        fullWidth
        margin="normal"
        label="Payout Provider"
        value={provider}
        disabled={isDisabled}
        onChange={(e) => setProvider(e.target.value)}
      >
        <MenuItem value="PAYPAL">PayPal</MenuItem>
        <MenuItem value="STRIPE" disabled>
          Stripe (Coming soon)
        </MenuItem>
      </TextField>

      <TextField
        fullWidth
        margin="normal"
        label="PayPal Email Address"
        value={identifier}
        disabled={isDisabled}
        onChange={(e) => setIdentifier(e.target.value)}
        helperText="Payments will be sent directly to this PayPal email."
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error.message}
        </Alert>
      )}

      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={
          isDisabled ||
          saving ||
          !identifier ||
          !hasChanges
        }
        onClick={handleSave}
      >
        Save Payout Settings
      </Button>
    </Box>
  );
}
